import {AbstractMessageHandler} from './AbstractMessageHandler.ts'
import {ChatMessageType} from '../enums'
import {isChatMessageType} from '../type-guards'
import type {Config, State} from '../types'
import {getParentColorScheme} from '../parentColorScheme'
import {getParentIsMobileViewport} from '../parentViewport'

const CHAT_OPEN_CLASS = 'alloy-webchat-widget-open'
const CHAT_LOADING_CLASS = 'alloy-webchat-widget-loading'

export class ChatMessageHandler extends AbstractMessageHandler {
  private readonly setUnreadCount?: (count: number) => void
  private readonly setVoiceCallActive?: (isActive: boolean) => void

  constructor(
    config: Config,
    state: State,
    sendMessageToButton: (type: string, payload: Record<string, unknown>) => void,
    sendMessageToChat: (type: string, payload: Record<string, unknown>) => void,
    toggleChat: () => void,
    dismissGreeting: () => void,
    scheduleGreeting: () => void,
    logError: (message: string, ...args: unknown[]) => void,
    showError: (message: string) => void,
    setUnreadCount?: (count: number) => void,
    setVoiceCallActive?: (isActive: boolean) => void
  ) {
    super(config, state, sendMessageToButton, sendMessageToChat, toggleChat, dismissGreeting, scheduleGreeting, logError, showError)
    this.setUnreadCount = setUnreadCount
    this.setVoiceCallActive = setVoiceCallActive
  }

  handle(_event: MessageEvent, type: string, payload?: Record<string, unknown>): void {
    try {
      if (!isChatMessageType(type)) {
        this.logError('Unknown chat message type:', type)
        return
      }

      switch (type) {
        case ChatMessageType.READY:
          this.handleReady()
          break
        case ChatMessageType.INITIALIZED:
          this.handleInitialized()
          break
        case ChatMessageType.ERROR:
          this.handleError(payload)
          break
        case ChatMessageType.CLOSE:
          this.handleClose()
          break
        case ChatMessageType.UNREAD_COUNT:
          this.handleUnreadCount(payload)
          break
        case ChatMessageType.VOICE_CALL_STATUS:
          this.handleVoiceCallStatus(payload)
          break
        case ChatMessageType.VOICE_CALL_MUTED:
          this.handleVoiceCallMuted(payload)
          break
        case ChatMessageType.CHAT_STATE_CHANGE:
          this.handleChatStateChange(payload)
          break
        case ChatMessageType.CHAT_VISIBLE:
          this.handleChatVisible()
          break
      }
    } catch (error) {
      this.logError('Error in ChatMessageHandler:', error)
    }
  }

  private handleReady(): void {
    try {
      if (this.state.chatIframe) {
        this.state.chatIframe.classList.add('ready')
      }

      if (
        this.state.settings &&
        !this.state.isInitialized &&
        !this.state.isInitializing &&
        this.state.chatIframe?.contentWindow
      ) {
        this.state.isInitializing = true
        this.sendMessageToChat('init', {
          apiUrl: this.config.apiUrl,
          iconAssetUrl: this.config.iconAssetUrl,
          wsUrl: this.config.wsUrl,
          apiKey: this.config.apiKey,
          eventId: this.config.eventId,
          contact: this.config.contact,
          settings: this.state.settings,
          isOpen: this.state.isOpen,
          showWidgetCloseButton: this.config.showWidgetCloseButton ?? true,
          language: this.state.language,
          parentColorScheme: getParentColorScheme(),
          parentIsMobile: getParentIsMobileViewport(),
        })
      }
    } catch (error) {
      this.logError('Error handling ready:', error)
      this.state.isInitializing = false
    }
  }

  private handleInitialized(): void {
    try {
      this.state.isInitialized = true
      this.state.isInitializing = false
      if (this.state.settings) {
        this.sendMessageToChat('applySettings', {
          settings: this.state.settings,
          parentColorScheme: getParentColorScheme(),
          parentIsMobile: getParentIsMobileViewport(),
        })
      }

      this.sendMessageToChat(this.state.isOpen ? 'open' : 'close', {})
      if (!this.state.isOpen) {
        this.scheduleGreeting()
      }
    } catch (error) {
      this.logError('Error handling initialized:', error)
      this.state.isInitializing = false
    }
  }

  private handleError(payload?: Record<string, unknown>): void {
    try {
      const errorMessage =
        payload &&
        typeof payload === 'object' &&
        'message' in payload &&
        typeof payload.message === 'string'
          ? payload.message
          : 'Unknown error'

      this.logError('Error from chat iframe:', errorMessage)
      if (errorMessage !== 'Unknown error') {
        this.showError(errorMessage)
      }
      this.finishLoading()
      this.state.isInitializing = false
    } catch (error) {
      this.logError('Error handling error message:', error)
    }
  }

  private handleClose(): void {
    try {
      this.toggleChat()
    } catch (error) {
      this.logError('Error handling close:', error)
    }
  }

  private handleUnreadCount(payload?: Record<string, unknown>): void {
    try {
      if (payload && typeof payload.count === 'number') {
        if (this.setUnreadCount) {
          this.setUnreadCount(payload.count)
        }
        this.sendMessageToButton('unreadCount', { count: payload.count })
      }
    } catch (error) {
      this.logError('Error handling unreadCount:', error)
    }
  }

  private handleVoiceCallStatus(payload?: Record<string, unknown>): void {
    try {
      if (payload && typeof payload.isActive === 'boolean') {
        if (this.setVoiceCallActive) {
          this.setVoiceCallActive(payload.isActive)
        }
        this.sendMessageToButton('voiceCallStatus', { isActive: payload.isActive })
      }
    } catch (error) {
      this.logError('Error handling voiceCallStatus:', error)
    }
  }

  private handleVoiceCallMuted(payload?: Record<string, unknown>): void {
    try {
      if (payload && typeof payload.isMuted === 'boolean') {
        this.sendMessageToButton('voiceCallMuted', { isMuted: payload.isMuted })
      }
    } catch (error) {
      this.logError('Error handling voiceCallMuted:', error)
    }
  }

  private handleChatStateChange(payload?: Record<string, unknown>): void {
    try {
      if (
        payload
        && typeof payload.isOpen === 'boolean'
        && payload.isOpen
        && payload.status === 'open'
        && this.state.isOpen
      ) {
        const iframeContainer = document.getElementById('alloy-webchat-widget-iframe-container')
        iframeContainer?.classList.add(CHAT_OPEN_CLASS)
        this.sendMessageToButton('hideIndicators', {})
      }
    } catch (error) {
      this.logError('Error handling chatStateChange:', error)
    }
  }

  private handleChatVisible(): void {
    try {
      if (this.state.isOpen) {
        this.finishLoading()
      }
    } catch (error) {
      this.logError('Error handling chatVisible:', error)
    }
  }

  private finishLoading(): void {
    const iframeContainer = document.getElementById('alloy-webchat-widget-iframe-container')
    iframeContainer?.classList.remove(CHAT_LOADING_CLASS)
  }
}
